import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { 
  FileText, 
  Calendar, 
  TrendingUp, 
  Download,
  Clock,
  Sparkles,
  ChevronRight,
  Building2,
  BarChart3,
  Award,
  Target,
  Users
} from "lucide-react";
import { OuvidoriaRelatorioMensal, MESES_NOMES } from "@/types/relatorio";
import { RelatorioCard } from "./RelatorioCard";
import { RelatorioVisualModal } from "./RelatorioVisualModal";
import { Skeleton } from "@/components/ui/skeleton";
import { motion } from "framer-motion";

export function RelatoriosList() {
  // Temporariamente sem autenticação - usar IDs mock
  const [loading, setLoading] = useState(true);
  const [relatorios, setRelatorios] = useState<OuvidoriaRelatorioMensal[]>([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedRelatorio, setSelectedRelatorio] = useState<OuvidoriaRelatorioMensal | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [tipoModal, setTipoModal] = useState<'mensal' | 'anual'>('mensal');

  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const years = [currentYear, currentYear - 1, currentYear - 2];

  const fetchRelatorios = useCallback(async () => {
    setLoading(true);
    try {
      const mock: OuvidoriaRelatorioMensal[] = Array.from({ length: 12 }, (_, idx) => {
        const total_solicitacoes = Math.floor(50 + Math.random() * 150);
        const total_concluidas = Math.floor(total_solicitacoes * 0.7);
        const total_pendentes = total_solicitacoes - total_concluidas;
        return {
          id: `${selectedYear}-${idx + 1}`,
          ouvidoria_id: "mock-ouvidoria",
          municipio_id: "mock-municipio",
          ano: selectedYear,
          mes: idx + 1,
          total_solicitacoes,
          total_concluidas,
          total_pendentes,
          media_tempo_primeira_resposta_horas: Number((1 + Math.random() * 3).toFixed(1)),
          media_tempo_encaminhamento_horas: Number((1 + Math.random() * 2).toFixed(1)),
          media_tempo_resolucao_horas: Number((24 + Math.random() * 48).toFixed(1)),
          percentual_primeira_resposta_dentro_prazo: Math.floor(80 + Math.random() * 15),
          percentual_resolucao_dentro_prazo: Math.floor(75 + Math.random() * 20),
          maior_tempo_primeira_resposta_horas: Number((6 + Math.random() * 24).toFixed(1)),
          maior_tempo_resolucao_horas: Number((48 + Math.random() * 120).toFixed(1)),
          media_atualizacoes_por_solicitacao: Number((1 + Math.random() * 3).toFixed(1)),
          media_intervalo_atualizacoes_horas: Number((6 + Math.random() * 12).toFixed(1)),
          gerado_em: new Date().toISOString(),
          gerado_por_sistema: true,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };
      });
      // Simula latência para UX
      await new Promise((res) => setTimeout(res, 300));
      setRelatorios(mock);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, [selectedYear]);

  useEffect(() => {
    // Buscar relatórios sem depender de profile
    void fetchRelatorios();
  }, [fetchRelatorios]);

  const handleVisualizarMensal = (mes: number) => {
    const relatorio = relatorios.find(r => r.mes === mes);
    if (relatorio) {
      setSelectedRelatorio(relatorio);
      setTipoModal('mensal');
      setModalOpen(true);
    } else {
      // Gerar dados mock para visualização
      const mockRelatorio: OuvidoriaRelatorioMensal = {
        id: crypto.randomUUID(),
        ouvidoria_id: '',
        municipio_id: '',
        ano: selectedYear,
        mes: mes,
        total_solicitacoes: Math.floor(Math.random() * 200) + 100,
        total_concluidas: Math.floor(Math.random() * 150) + 80,
        total_pendentes: Math.floor(Math.random() * 50) + 10,
        media_tempo_primeira_resposta_horas: Math.random() * 24 + 2,
        media_tempo_encaminhamento_horas: Math.random() * 12 + 1,
        media_tempo_resolucao_horas: Math.random() * 72 + 24,
        percentual_primeira_resposta_dentro_prazo: Math.random() * 20 + 80,
        percentual_resolucao_dentro_prazo: Math.random() * 25 + 75,
        maior_tempo_primeira_resposta_horas: Math.random() * 48 + 12,
        maior_tempo_resolucao_horas: Math.random() * 120 + 48,
        media_atualizacoes_por_solicitacao: Math.random() * 5 + 2,
        media_intervalo_atualizacoes_horas: Math.random() * 24 + 6,
        gerado_em: new Date().toISOString(),
        gerado_por_sistema: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      setSelectedRelatorio(mockRelatorio);
      setTipoModal('mensal');
      setModalOpen(true);
    }
  };

  const handleVisualizarAnual = () => {
    // Gerar resumo anual
    const mockRelatorioAnual: OuvidoriaRelatorioMensal = {
      id: crypto.randomUUID(),
      ouvidoria_id: '',
      municipio_id: '',
      ano: selectedYear,
      mes: 12,
      total_solicitacoes: 4888,
      total_concluidas: 4598,
      total_pendentes: 290,
      media_tempo_primeira_resposta_horas: 8.5,
      media_tempo_encaminhamento_horas: 4.2,
      media_tempo_resolucao_horas: 52.3,
      percentual_primeira_resposta_dentro_prazo: 94.2,
      percentual_resolucao_dentro_prazo: 91.5,
      maior_tempo_primeira_resposta_horas: 72,
      maior_tempo_resolucao_horas: 168,
      media_atualizacoes_por_solicitacao: 3.8,
      media_intervalo_atualizacoes_horas: 18.5,
      gerado_em: new Date().toISOString(),
      gerado_por_sistema: true,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    setSelectedRelatorio(mockRelatorioAnual);
    setTipoModal('anual');
    setModalOpen(true);
  };

  const mesesDisponiveis = Array.from({ length: 12 }, (_, i) => {
    const mes = i + 1;
    const disponivel = selectedYear < currentYear || 
      (selectedYear === currentYear && mes <= currentMonth);
    const relatorio = relatorios.find(r => r.mes === mes);
    return {
      mes,
      nome: MESES_NOMES[i],
      disponivel,
      relatorio
    };
  });

  // Cálculo de estatísticas do ano
  const totalSolicitacoesAno = relatorios.reduce((acc, r) => acc + r.total_solicitacoes, 0) || 2456;
  const totalConcluidasAno = relatorios.reduce((acc, r) => acc + r.total_concluidas, 0) || 2198;
  const mediaSLA = relatorios.length > 0 
    ? relatorios.reduce((acc, r) => acc + (r.percentual_resolucao_dentro_prazo || 0), 0) / relatorios.length
    : 89.5;

  return (
    <>
      <div className="space-y-8">
        {/* Header Section */}
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-primary via-primary-dark to-secondary p-8 text-primary-foreground">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImEiIHBhdHRlcm5Vbml0cz0idXNlclNwYWNlT25Vc2UiIHdpZHRoPSIyMCIgaGVpZ2h0PSIyMCI+PHBhdGggZD0iTTAgMGgyMHYyMEgweiIgZmlsbD0ibm9uZSIvPjxjaXJjbGUgY3g9IjEwIiBjeT0iMTAiIHI9IjEiIGZpbGw9InJnYmEoMjU1LDI1NSwyNTUsMC4xKSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3QgZmlsbD0idXJsKCNhKSIgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIvPjwvc3ZnPg==')] opacity-30" />
          <div className="absolute -top-24 -right-24 w-80 h-80 bg-white/5 rounded-full blur-3xl" />
          <div className="absolute -bottom-24 -left-24 w-64 h-64 bg-white/5 rounded-full blur-3xl" />
          
          <div className="relative">
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-5">
                <div className="w-20 h-20 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center shadow-lg">
                  <BarChart3 className="h-10 w-10" />
                </div>
                <div>
                  <h2 className="text-3xl font-heading font-bold">Central de Relatórios</h2>
                  <p className="text-primary-foreground/80 mt-1 text-lg">
                    Acompanhe o desempenho da sua ouvidoria com dados detalhados
                  </p>
                </div>
              </div>
              
              <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(Number(v))}>
                <SelectTrigger className="w-[160px] bg-white/20 border-white/30 text-primary-foreground h-12 text-base">
                  <Calendar className="h-5 w-5 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {years.map(year => (
                    <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Stats do ano em destaque */}
            <div className="grid grid-cols-4 gap-4">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-5 border border-white/10">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                    <FileText className="h-5 w-5" />
                  </div>
                  <p className="text-sm text-primary-foreground/70">Total do Ano</p>
                </div>
                <p className="text-3xl font-heading font-bold">{totalSolicitacoesAno.toLocaleString()}</p>
                <p className="text-xs text-primary-foreground/60 mt-1">solicitações recebidas</p>
              </div>
              
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-5 border border-white/10">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                    <Target className="h-5 w-5" />
                  </div>
                  <p className="text-sm text-primary-foreground/70">Resolvidas</p>
                </div>
                <p className="text-3xl font-heading font-bold">{totalConcluidasAno.toLocaleString()}</p>
                <p className="text-xs text-primary-foreground/60 mt-1">
                  {((totalConcluidasAno / totalSolicitacoesAno) * 100).toFixed(1)}% do total
                </p>
              </div>
              
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-5 border border-white/10">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                    <Award className="h-5 w-5" />
                  </div>
                  <p className="text-sm text-primary-foreground/70">SLA Médio</p>
                </div>
                <p className="text-3xl font-heading font-bold">{mediaSLA.toFixed(1)}%</p>
                <p className="text-xs text-primary-foreground/60 mt-1">dentro do prazo</p>
              </div>
              
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-5 border border-white/10">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                    <Calendar className="h-5 w-5" />
                  </div>
                  <p className="text-sm text-primary-foreground/70">Relatórios</p>
                </div>
                <p className="text-3xl font-heading font-bold">
                  {relatorios.length} <span className="text-lg font-normal">/ 12</span>
                </p>
                <p className="text-xs text-primary-foreground/60 mt-1">meses com dados</p>
              </div>
            </div>
          </div>
        </div>

        <Tabs defaultValue="mensais" className="w-full">
          <TabsList className="mb-6 h-12">
            <TabsTrigger value="mensais" className="gap-2 h-10 px-6 text-base">
              <Calendar className="h-5 w-5" />
              Relatórios Mensais
            </TabsTrigger>
            <TabsTrigger value="anual" className="gap-2 h-10 px-6 text-base">
              <Sparkles className="h-5 w-5" />
              Relatório Anual
            </TabsTrigger>
          </TabsList>

          <TabsContent value="mensais" className="space-y-6">
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {Array.from({ length: 12 }).map((_, i) => (
                  <Skeleton key={i} className="h-[280px] rounded-2xl" />
                ))}
              </div>
            ) : (
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
              >
                {mesesDisponiveis.map(({ mes, nome, disponivel, relatorio }, index) => (
                  <motion.div
                    key={mes}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <RelatorioCard
                      tipo="mensal"
                      ano={selectedYear}
                      mes={mes}
                      disponivel={disponivel}
                      geradoEm={relatorio?.gerado_em}
                      relatorio={relatorio}
                      onVisualizar={() => handleVisualizarMensal(mes)}
                    />
                  </motion.div>
                ))}
              </motion.div>
            )}
          </TabsContent>

          <TabsContent value="anual">
            <Card className="overflow-hidden border-2 border-primary/10">
              <div className="bg-gradient-to-r from-primary/5 via-secondary/5 to-tertiary/5 p-8">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-5">
                    <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-primary to-primary-dark text-primary-foreground flex items-center justify-center shadow-lg shadow-primary/25">
                      <Sparkles className="h-10 w-10" />
                    </div>
                    <div>
                      <h3 className="font-heading font-bold text-2xl text-foreground">
                        Relatório Anual {selectedYear}
                      </h3>
                      <p className="text-muted-foreground text-lg mt-1">
                        Consolidado completo de todos os meses do ano
                      </p>
                    </div>
                  </div>
                  
                  <Badge 
                    variant={selectedYear === currentYear ? "secondary" : "default"}
                    className="text-base px-4 py-2"
                  >
                    {selectedYear === currentYear ? "Em andamento" : "Disponível"}
                  </Badge>
                </div>
              </div>
              
              <CardContent className="p-8">
                <div className="flex items-center justify-between">
                  <div className="space-y-2">
                    <p className="text-muted-foreground text-lg">
                      {selectedYear === currentYear 
                        ? `Dados parciais até ${MESES_NOMES[currentMonth - 1]}`
                        : 'Relatório completo do ano'
                      }
                    </p>
                    <div className="flex items-center gap-6 text-sm text-muted-foreground">
                      <span className="flex items-center gap-2">
                        <Clock className="h-5 w-5" />
                        Atualizado mensalmente
                      </span>
                      <span className="flex items-center gap-2">
                        <Users className="h-5 w-5" />
                        {totalSolicitacoesAno.toLocaleString()} solicitações
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex gap-3">
                    <Button variant="outline" size="lg" className="gap-2 h-12 px-6">
                      <Download className="h-5 w-5" />
                      Exportar
                    </Button>
                    <Button size="lg" className="gap-2 h-12 px-6" onClick={handleVisualizarAnual}>
                      Visualizar Relatório
                      <ChevronRight className="h-5 w-5" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Benefícios do relatório anual */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
              <Card className="p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Building2 className="h-7 w-7 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-heading font-semibold text-lg">Visão Institucional</h4>
                    <p className="text-muted-foreground mt-2">
                      Dados consolidados para prestação de contas, transparência pública e relatórios oficiais
                    </p>
                  </div>
                </div>
              </Card>
              <Card className="p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-secondary/10 flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="h-7 w-7 text-secondary" />
                  </div>
                  <div>
                    <h4 className="font-heading font-semibold text-lg">Tendências Anuais</h4>
                    <p className="text-muted-foreground mt-2">
                      Análise de evolução ao longo do ano e identificação de padrões de atendimento
                    </p>
                  </div>
                </div>
              </Card>
              <Card className="p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-tertiary/10 flex items-center justify-center flex-shrink-0">
                    <FileText className="h-7 w-7 text-tertiary" />
                  </div>
                  <div>
                    <h4 className="font-heading font-semibold text-lg">Pronto para Apresentações</h4>
                    <p className="text-muted-foreground mt-2">
                      Formato profissional ideal para reuniões, apresentações e relatórios oficiais
                    </p>
                  </div>
                </div>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <RelatorioVisualModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        relatorio={selectedRelatorio}
        tipo={tipoModal}
        municipioNome="Município"
        ouvidoriaNome="Ouvidoria Municipal"
      />
    </>
  );
}
